import { LinkButton, SearchInput, Text, Pagination, Tooltip } from '$components'
import { useDebouncedSearch } from '$hooks/use-debounced-search'
import { ElementIntent } from '$components'
import { BinIcon, EyeIcon, PlusIcon } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { ServicesFilterParam } from '../enums/filter-params'
import { DescriptionCell } from '../components/cells/description-cell'
import { PageLayout } from 'src/layouts/page'
import { AnimatedOutlet } from '$components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Role, Service } from '$api-client/types.gen'
import { ChipCell } from '$components'
import { ColourCell } from '$components'
import { usePage } from '$hooks/use-page'
import { serviceDeleteMutation, serviceIndexOptions, serviceIndexQueryKey } from '$api-client/@tanstack/react-query.gen'
import { ConfirmationModal } from '$components'
import { useMemo, useState } from 'react'
import { Router } from '$utils/router'
import { ToastHelper } from '$components'
import { ToastType } from '$components'
import { ServiceRoutePath } from '../routes/protected'
import { useNavigate } from 'react-router'
import { useAuth } from '$features/auth/hooks/use-auth'
import { PermissionsHelper } from '$utils/permissions'
import { GenericTable } from '$components'
import { OptionsCell } from '$components'
import { useTenant } from '$features/auth/hooks/use-tenant'
import { SUBSCRIPTION_EXPIRED_MESSAGE } from '$constants/subscription'
import { useTableSort } from '$hooks/use-table-sort'
import { usePageTitle } from '$components'
import { NoServicesEmptyState } from '../components/no-services-empty-state'
import { TableSkeleton } from '$components'

const columnHelper = createColumnHelper<Service>()

export type DeleteConfirmationModalState = {
  isOpen: boolean,
  serviceId: number | null,
}

export function ServicesPage() {
  usePageTitle('Services')

  const navigate = useNavigate()
  const { getSorting } = useTableSort()
  const queryClient = useQueryClient()
  const { page, setPage } = usePage()
  const { user } = useAuth()
  const { hasSubscriptionExpired } = useTenant()

  const [deleteConfirmationModalState, setDeleteConfirmationModalState] = useState<DeleteConfirmationModalState>({
    isOpen: false,
    serviceId: null,
  })

  const { search, setSearch, debouncedSearch } = useDebouncedSearch({
    searchParam: ServicesFilterParam.Search,
    initialValue: '',
    onSearchChange: () => setPage(1),
  })

  const { data: services, isLoading: isServicesLoading } = useQuery({
    ...serviceIndexOptions({
      query: {
        query: debouncedSearch,
        page: page,
        sort: getSorting(),
      },
    }),
  })

  const { mutate: deleteService, isPending: isDeletingService } = useMutation({
    ...serviceDeleteMutation(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: serviceIndexQueryKey() })
      setDeleteConfirmationModalState({ isOpen: false, serviceId: null })
      ToastHelper.deleteSuccess(ToastType.Service)
    },
    onError: () => ToastHelper.deleteError(ToastType.Service),
  })

  const handleConfirmDelete = () => {
    deleteService({
      path: {
        id: deleteConfirmationModalState.serviceId!,
      },
    })
  }

  const columns = [
    columnHelper.accessor('values.name', {
      id: 'name',
      header: 'Name',
      cell: ({ row, getValue }) => <ColourCell
        colour={row.original.colour}
        name={getValue()}
      />,
    }),
    columnHelper.accessor('values.description', {
      id: 'description',
      header: 'Description',
      cell: ({ getValue }) => (
        <DescriptionCell>
          { getValue() }
        </DescriptionCell>
      ),
    }),
    columnHelper.display({
      id: 'pricing',
      header: 'Pricing',
      enableSorting: false,
      cell: ({ row }) => <ChipCell values={[row.original.values!.pricing_type]} />,
    }),
    columnHelper.display({
      id: 'actions',
      minSize: 66,
      maxSize: 66,
      cell: ({ row }) => (
        <OptionsCell
          options={[
            {
              id: 'view',
              value: 'View',
              icon: EyeIcon,
              onClick: () => navigate(Router.getRoute(ServiceRoutePath.ServicesView, { id: row.original.id })),
            },
            {
              id: 'delete',
              intent: ElementIntent.Danger,
              value: 'Delete',
              onClick: () => setDeleteConfirmationModalState({ isOpen: true, serviceId: row.original.id }),
              icon: BinIcon,
              disabled: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN]),
              tooltip: PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
                ? 'You are not allowed to delete services.'
                : undefined,
            },
          ]}
        />
      ),
    }),
  ]

  const data = useMemo(() => services?.data ?? [], [services])

  const isCreateButtonDisabled = hasSubscriptionExpired || PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
  const tooltipContent = hasSubscriptionExpired
    ? SUBSCRIPTION_EXPIRED_MESSAGE
    : PermissionsHelper.doesntHaveRole(user.role, [Role.SUPER_ADMIN, Role.ADMIN])
      ? 'You are not allowed to create new services.'
      : undefined

  return (
    <PageLayout>
      {/* Header */}
      <div className="grid grid-cols-1 grid-rows-[auto_1fr] h-full">
        <div className="pb-stack-roomy flex sm:flex-row flex-col sm:items-center justify-between gap-stack-roomy sm:gap-stack-tight">
          <Text
            as="h1"
            weight="medium"
          >
            Services
          </Text>

          <div className="flex gap-stack-tight items-center">
            <SearchInput
              value={search}
              onChange={setSearch}
              className="w-full sm:w-52"
            />

            <Tooltip
              content={tooltipContent}
              disabled={!isCreateButtonDisabled}
            >
              <LinkButton
                to={ServiceRoutePath.ServiceCreate}
                icon={PlusIcon}
                disabled={isCreateButtonDisabled}
              >
                New Service
              </LinkButton>
            </Tooltip>
          </div>
        </div>

        {/* Page Content */}
        {isServicesLoading && (
          <TableSkeleton
            columns={4}
            rows={10}
          />
        )}

        {!isServicesLoading && data.length === 0 && (
          <NoServicesEmptyState isNoResults={debouncedSearch.length > 0} />
        )}

        {data.length > 0 && (
          <GenericTable.Container>
            <GenericTable
              data={data}
              columns={columns}
              rowLink={row => Router.getRoute(ServiceRoutePath.ServicesView, { id: row.id })}
            />

            <Pagination
              currentPage={page}
              onChange={setPage}
              totalPages={services?.meta.last_page}
              totalItems={services?.meta.total}
              itemsPerPage={services?.meta.per_page}
              currentItemCount={services?.meta.current_page}
            />
          </GenericTable.Container>
        )}
      </div>

      <AnimatedOutlet />

      { deleteConfirmationModalState.isOpen && (
        <ConfirmationModal
          onClose={() => setDeleteConfirmationModalState({ isOpen: false, serviceId: null })}
          onConfirm={handleConfirmDelete}
          isLoading={isDeletingService}
          title="Delete Service"
          description="Are you sure you want to delete this service?"
          intent={ElementIntent.Danger}
        />
      )}
    </PageLayout>
  )
}
